import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  StatusBar,
  Modal,
  ActivityIndicator,
  Alert,
  ScrollView,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../screens/ThemeContext';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';
import { useAuth } from '../private/AuthContext';
import CampayPaymentForm from '../services/CampayPaymentForm';
import PurchaseService from '../services/PurchaseService';

const paymentMethods = [
  {
    id: 'orange',
    name: 'Orange Money',
    icon: 'cellphone',
    color: '#FF6F00',
  },
  {
    id: 'mtn',
    name: 'Mobile Money',
    icon: 'cellphone',
    color: '#FFCA28',
  },
];

export default function PaymentScreen({ route, navigation }) {
  const { document, documentDetail } = route.params;
  const { theme } = useTheme();
  const { user } = useAuth();
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [showCampayForm, setShowCampayForm] = useState(false);

  useEffect(() => {
    console.log('Document reçu:', document);
    console.log('Document Detail reçu:', documentDetail);
  }, [document, documentDetail]);

  useEffect(() => {
    if (!user) {
      Alert.alert(
        'Connexion requise',
        'Vous devez être connecté pour effectuer cet achat.',
        [
          {
            text: 'Annuler',
            onPress: () => navigation.goBack(),
            style: 'cancel',
          },
          {
            text: 'Se connecter',
            onPress: () => {
              navigation.navigate('LoginScreen', {
                redirectAfter: {
                  screen: 'PaymentScreen',
                  params: { document, documentDetail }
                }
              });
            },
          },
        ]
      );
    }
  }, [user]);

  const isFreeDocument = documentDetail?.gratuit || document.type === 'arretes';

  const handlePaymentSuccess = (purchaseData) => {
    console.log('Paiement réussi:', purchaseData);
    setSuccessModalVisible(true);
  };

  const handlePaymentError = (error) => {
    console.error('Erreur de paiement:', error);
    Alert.alert('Erreur', 'Une erreur est survenue lors du paiement. Veuillez réessayer.');
  };

  // Fonction simplifiée pour les documents gratuits — utilise PurchaseService
  const handleFreeDocument = async () => {
    if (!user) {
      Alert.alert('Erreur', 'Vous devez être connecté pour accéder à ce document');
      return;
    }

    try {
      setLoading(true);

      let purchaseData = {
        userId: user.uid,
        documentId: document.id,
        documentType: document.type === 'simple' ? 'documents' : document.type,
        gratuit: true,
      };

      // Ajouter les données spécifiques selon le type de document
      if (document.type === 'concours' && documentDetail) {
        purchaseData = {
          ...purchaseData,
          titre: documentDetail.titre,
          annee: documentDetail.annee,
          filiere: documentDetail.filiere,
          nombrePages: documentDetail.nombrePages,
          prix: documentDetail.prixFinal || documentDetail.prix,
          ...(documentDetail.groupId ? { groupId: documentDetail.groupId } : {}),
        };
      } else if (document.type === 'bords') {
        purchaseData = {
          ...purchaseData,
          titre: document.titre,
          prix: document.prixFinal || document.prix,
          nombrePages: document.nombrePages,
        };
      }

      // Utiliser PurchaseService qui vérifie les doublons automatiquement
      await PurchaseService.savePurchase(purchaseData);

      // Afficher le modal de succès
      setSuccessModalVisible(true);
    } catch (error) {
      console.error('Error processing free document:', error);
      Alert.alert('Erreur', 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayNowPress = () => {
    if (isFreeDocument) {
      handleFreeDocument();
    } else if (selectedMethod) {
      setShowCampayForm(true);
    } else {
      Alert.alert('Sélection requise', 'Veuillez sélectionner une méthode de paiement');
    }
  };

  const renderSuccessModal = () => (
    <Modal
      visible={successModalVisible}
      transparent={true}
      animationType="fade"
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContent, { backgroundColor: theme.surface }]}>
          <View style={[styles.successIcon, { backgroundColor: theme.primary + '20' }]}>
            <MaterialCommunityIcons name="check-circle" size={50} color={theme.primary} />
          </View>
          <Text style={[styles.modalTitle, { color: theme.text }]}>
            {isFreeDocument ? 'Document disponible !' : 'Achat réussi !'}
          </Text>
          <Text style={[styles.modalMessage, { color: theme.textSecondary }]}>
            Votre document est maintenant disponible
          </Text>
          <TouchableOpacity
            style={[styles.modalButton, { backgroundColor: theme.primary }]}
            onPress={() => {
              setSuccessModalVisible(false);
              navigation.goBack();
            }}
          >
            <Text style={styles.modalButtonText}>Accéder au document</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  if (!user) return null;

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: theme.background }]}>
      <StatusBar barStyle={theme.statusBar} backgroundColor={theme.background} />
      
      <View style={styles.header}>
        <TouchableOpacity 
          style={[styles.backButton, { backgroundColor: theme.surface }]}
          onPress={() => {
            if (showCampayForm) {
              setShowCampayForm(false);
            } else {
              navigation.goBack();
            }
          }}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.primary} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: theme.text }]}>
          {showCampayForm 
            ? `Paiement via ${selectedMethod === 'mtn' ? 'Mobile Money' : 'Orange Money'}`
            : (isFreeDocument ? 'Document Gratuit' : 'Choisir le mode de paiement')}
        </Text>
      </View>

      <ScrollView style={styles.content}>
        {!showCampayForm ? (
          <>
            <View style={[styles.documentInfo, { backgroundColor: theme.surface, borderColor: theme.border }]}>
              <Text style={[styles.documentTitle, { color: theme.text }]}>
                {documentDetail?.titre || document.sigle || document.titre} {documentDetail?.annee && `(${documentDetail.annee})`}
              </Text>
              {documentDetail?.filiere && (
                <Text style={[styles.documentFiliere, { color: theme.textSecondary }]}>
                  Filière: {documentDetail.filiere}
                </Text>
              )}
              {documentDetail?.groupId && (
                <Text style={[styles.documentGroup, { color: theme.textSecondary }]}>
                  Groupe: {document.groups.find(g => g.id === documentDetail.groupId)?.nom}
                </Text>
              )}
              <Text style={[styles.documentType, { color: theme.textSecondary }]}>
                {document.type === 'concours' ? 'Sujet de Concours' :
                 document.type === 'bords' ? 'Bord' :
                 'Arrêté'}
              </Text>
              {/* Affichage du prix avec réduction */}
              {isFreeDocument ? (
                <Text style={[styles.price, { color: theme.primary }]}>
                  Gratuit
                </Text>
              ) : (
                (() => {
                  const prix = documentDetail?.prix || document.prix;
                  const prixFinal = documentDetail?.prixFinal || document.prixFinal;
                  const prixInitial = documentDetail?.prixInitial || document.prixInitial;
                  const reductionPourcent = parseFloat(documentDetail?.reductionPourcent || document.reductionPourcent || 0);

                  if (reductionPourcent > 0 && prixFinal) {
                    return (
                      <View>
                        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                          <View style={{
                            backgroundColor: theme.danger,
                            paddingHorizontal: 8,
                            paddingVertical: 4,
                            borderRadius: 6,
                            marginRight: 10
                          }}>
                            <Text style={{ color: '#fff', fontSize: 12, fontWeight: 'bold' }}>
                              -{reductionPourcent}%
                            </Text>
                          </View>
                          <Text style={[styles.price, {
                            color: theme.textSecondary,
                            textDecorationLine: 'line-through',
                            fontSize: 18
                          }]}>
                            {prixInitial || prix} FCFA
                          </Text>
                        </View>
                        <Text style={[styles.price, { color: theme.primary }]}>
                          {prixFinal} FCFA
                        </Text>
                      </View>
                    );
                  } else {
                    return (
                      <Text style={[styles.price, { color: theme.primary }]}>
                        {prix} FCFA
                      </Text>
                    );
                  }
                })()
              )}
            </View>

            {!isFreeDocument && (
              <View style={styles.methodsContainer}>
                {paymentMethods.map((method) => (
                  <TouchableOpacity
                    key={method.id}
                    style={[
                      styles.methodCard,
                      { 
                        backgroundColor: theme.surface,
                        borderColor: selectedMethod === method.id ? method.color : theme.border
                      }
                    ]}
                    onPress={() => setSelectedMethod(method.id)}
                  >
                    <View style={[styles.methodIcon, { backgroundColor: method.color + '20' }]}>
                      <MaterialCommunityIcons name={method.icon} size={30} color={method.color} />
                    </View>
                    <Text style={[styles.methodName, { color: theme.text }]}>{method.name}</Text>
                    {selectedMethod === method.id && (
                      <MaterialCommunityIcons name="check-circle" size={24} color={method.color} />
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </>
        ) : (
          <CampayPaymentForm
            amount={documentDetail?.prixFinal || document.prixFinal || documentDetail?.prix || document.prix}
            document={document}
            documentDetail={documentDetail}
            userId={user.uid}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            theme={theme}
            paymentMethod={selectedMethod}
            navigation={navigation}
          />
        )}
      </ScrollView>

      {!showCampayForm && (
        <View style={[styles.bottomBar, { backgroundColor: theme.surface }]}>
          <TouchableOpacity
            style={[
              styles.payButton,
              { opacity: (!isFreeDocument && !selectedMethod) || loading ? 0.5 : 1 }
            ]}
            onPress={handlePayNowPress}
            disabled={(!isFreeDocument && !selectedMethod) || loading}
          >
            <LinearGradient
              colors={[theme.primary, theme.primaryDark || theme.primary]}
              style={styles.payButtonGradient}
            >
              {loading ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.payButtonText}>
                  {isFreeDocument ? 'Télécharger' : 'Continuer'}
                </Text>
              )}
            </LinearGradient>
          </TouchableOpacity>
        </View>
      )}

      {renderSuccessModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  backButton: {
    padding: 8,
    borderRadius: 8,
    marginRight: 16,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  documentInfo: {
    padding: 20,
    borderRadius: 16,
    marginBottom: 24,
    borderWidth: 1,
  },
  documentTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  documentFiliere: {
    fontSize: 14,
    marginBottom: 4,
  },
  documentGroup: {
    fontSize: 14,
    marginBottom: 4,
    fontStyle: 'italic',
  },
  documentType: {
    fontSize: 14,
    marginBottom: 12,
  },
  price: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  methodsContainer: {
    gap: 16,
  },
  methodCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 16,
    borderWidth: 2,
  },
  methodIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  methodName: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
  },
  bottomBar: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0, 0, 0, 0.1)',
  },
  payButton: {
    overflow: 'hidden',
    borderRadius: 12,
  },
  payButtonGradient: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  payButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: '80%',
    padding: 24,
    borderRadius: 20,
    alignItems: 'center',
  },
  successIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  modalMessage: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
  },
  modalButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 12,
  },
  modalButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});